import React from "react";
import { motion } from "framer-motion";
import { Building, MapPin, Check } from "lucide-react";
import Container from "./Container";

const ProjectsSima: React.FC = () => {
  return (
    <section className="bg-white py-24 md:py-32 relative" id="proyectos">
      <Container size="xl">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <span className="mb-4 inline-block font-medium text-accent uppercase tracking-widest text-sm">
            Proyectos Destacados
          </span>
          <h2 className="font-display text-4xl font-bold text-primary md:text-5xl lg:text-6xl mb-6">
            Oportunidades Inmobiliarias
          </h2>
          <p className="text-lg text-text-secondary">
            Te presentamos dos proyectos clave en Punta Cana, pensados tanto
            para inversión como para estilo de vida residencial o renta
            vacacional.
          </p>
        </div>

        {/* ORION PROJECT */}
        <div className="group grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16 items-center mb-32 rounded-[2.5rem] bg-white p-6 md:p-10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] border border-border-light transition-all duration-700 hover:shadow-[0_40px_80px_-15px_rgba(0,0,0,0.1)]">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 order-2 lg:order-1 h-full"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-2xl relative h-full min-h-[400px] xl:min-h-[500px]">
              <img
                src="/assets/ORION_BAV.jpg"
                alt="ORIÓN Punta Cana Residences"
                className="w-full h-full object-cover transition-transform duration-[1.5s] ease-[0.16,1,0.3,1] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <span className="bg-accent/90 backdrop-blur-sm text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest shadow-lg">
                  Costa Bávaro
                </span>
                <h3 className="text-white font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
                  ORIÓN Residences
                </h3>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 order-1 lg:order-2 space-y-8"
          >
            <div className="flex items-center gap-3 text-text-primary bg-background-alt px-5 py-3 rounded-xl w-fit border border-border-light">
              <MapPin className="h-5 w-5 text-accent" />
              <span className="font-semibold text-sm uppercase tracking-widest">
                Ubicación Estratégica
              </span>
            </div>

            <p className="text-text-secondary text-xl font-light leading-relaxed">
              Proyecto residencial pensado para un estilo de vida práctico y
              sofisticado en una de las zonas más dinámicas de Punta Cana.
            </p>

            <div className="bg-background-alt p-8 rounded-3xl border border-border-light space-y-5 shadow-inner">
              <div className="flex justify-between items-end pb-5 border-b border-border-light/50">
                <span className="text-text-secondary font-medium uppercase tracking-wider text-sm">
                  Inversión Desde
                </span>
                <span className="font-bold text-3xl md:text-4xl text-primary tracking-tight">
                  US$139,000
                </span>
              </div>
              <div className="flex justify-between items-center pb-5 border-b border-border-light/50">
                <span className="text-text-secondary font-medium">
                  Reserva Inicial
                </span>
                <span className="font-bold text-xl text-primary">US$5,000</span>
              </div>
              <div className="flex justify-between items-center pb-5 border-b border-border-light/50">
                <span className="text-text-secondary font-medium">
                  Tipologías
                </span>
                <span className="text-primary font-semibold text-right">
                  Aptos. y Penthouses (1 y 2 hab)
                </span>
              </div>
              <div className="pt-3">
                <span className="text-text-secondary block mb-4 font-semibold uppercase tracking-wider text-sm">
                  Amenidades Premium
                </span>
                <div className="flex flex-wrap gap-2.5">
                  {[
                    "Rooftop",
                    "Piscina Infinity",
                    "BBQ",
                    "Tumbonas",
                    "Gimnasio",
                  ].map((amenity, i) => (
                    <span
                      key={i}
                      className="bg-white border border-border-light px-4 py-2 rounded-xl text-sm font-medium text-primary flex items-center gap-2 shadow-sm transition-colors hover:border-accent/50 hover:bg-background-alt"
                    >
                      <Check className="h-4 w-4 text-accent" />
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <a
              href="#agenda"
              className="group inline-flex w-full items-center justify-center rounded-xl bg-primary px-8 py-5 font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-primary-light hover:shadow-[0_20px_40px_-15px_rgba(10,37,64,0.5)] hover:-translate-y-1"
            >
              <Building className="mr-3 h-5 w-5" />
              Consultar Disponibilidad
            </a>
          </motion.div>
        </div>

        {/* FOREST PROJECT */}
        <div className="group grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16 items-center rounded-[2.5rem] bg-white p-6 md:p-10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] border border-border-light transition-all duration-700 hover:shadow-[0_40px_80px_-15px_rgba(0,0,0,0.1)]">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 space-y-8"
          >
            <div className="flex items-center gap-3 text-text-primary bg-background-alt px-5 py-3 rounded-xl w-fit border border-border-light">
              <MapPin className="h-5 w-5 text-accent" />
              <span className="font-semibold text-sm uppercase tracking-widest">
                Masterplan en Punta Cana
              </span>
            </div>

            <p className="text-text-secondary text-xl font-light leading-relaxed">
              Un concepto de comunidad exclusivo con amenidades completas.
              Perfecto para quienes buscan un estilo de vida activo y funcional
              en contacto con la naturaleza.
            </p>

            <div className="bg-background-alt p-8 rounded-3xl border border-border-light space-y-5 shadow-inner">
              <div className="flex justify-between items-end pb-5 border-b border-border-light/50">
                <span className="text-text-secondary font-medium uppercase tracking-wider text-sm">
                  Inversión Desde
                </span>
                <span className="font-bold text-3xl md:text-4xl text-primary tracking-tight">
                  US$125,000
                </span>
              </div>
              <div className="flex justify-between items-center pb-5 border-b border-border-light/50">
                <span className="text-text-secondary font-medium">
                  Reserva Inicial
                </span>
                <span className="font-bold text-xl text-primary">US$2,000</span>
              </div>
              <div className="flex justify-between items-center pb-5 border-b border-border-light/50">
                <span className="text-text-secondary font-medium">
                  Tipologías
                </span>
                <span className="text-primary font-semibold text-right">
                  2 hab (3 variaciones)
                </span>
              </div>
              <div className="pt-3">
                <span className="text-text-secondary block mb-4 font-semibold uppercase tracking-wider text-sm">
                  Amenidades Premium
                </span>
                <div className="flex flex-wrap gap-2.5">
                  {[
                    "Piscinas",
                    "Gimnasio",
                    "Pádel",
                    "Kids & Pet Park",
                    "Clubhouse / BBQ",
                  ].map((amenity, i) => (
                    <span
                      key={i}
                      className="bg-white border border-border-light px-4 py-2 rounded-xl text-sm font-medium text-primary flex items-center gap-2 shadow-sm transition-colors hover:border-accent/50 hover:bg-background-alt"
                    >
                      <Check className="h-4 w-4 text-accent" />
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <a
              href="#agenda"
              className="group inline-flex w-full items-center justify-center rounded-xl bg-primary px-8 py-5 font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-primary-light hover:shadow-[0_20px_40px_-15px_rgba(10,37,64,0.5)] hover:-translate-y-1"
            >
              <Building className="mr-3 h-5 w-5" />
              Consultar Disponibilidad
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 h-full"
          >
            <div className="rounded-[2rem] overflow-hidden shadow-2xl relative h-full min-h-[400px] xl:min-h-[500px]">
              <img
                src="/assets/FOREST_BAV.jpeg"
                alt="FOREST Punta Cana Residences"
                className="w-full h-full object-cover transition-transform duration-[1.5s] ease-[0.16,1,0.3,1] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8 text-right">
                <span className="bg-accent/90 backdrop-blur-sm text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest shadow-lg">
                  Punta Cana
                </span>
                <h3 className="text-white font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
                  FOREST Comunidad
                </h3>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default ProjectsSima;
